// utils/dateUtils.js
import moment from 'moment';
import { ROUTE_CONFIG } from '../config/routeConfig';
import { isEven, findNextAppropriateDate } from '../../../../../services/common';
/**
 * Check if week number is even
 */
export const isEvenWeek = (date) => {
  return isEven(date)
};

export const findNextAppropriateVisitDate = (date) => {
  return findNextAppropriateDate(date)
}
/**
 * Get week type (Even/Odd)
 */
export const getWeekType = (date) => {
  return isEvenWeek(date) ? 'Even' : 'Odd';
};

/**
 * Get day of week string
 */
export const getDayOfWeek = (date) => {
  return moment(date).format('dddd');
};

/**
 * Check if date is a weekday
 */
export const isWeekday = (date) => {
  const day = moment(date).day();
  return day >= 1 && day <= 5;
};

/**
 * Find next matching date for a job based on frequency
 */
export const findNextMatchingDate = (job, fromDate = new Date()) => {
  const startDate = moment(fromDate);
  let nextDate = startDate.clone();
  
  switch (job.frequency) {
    case 'Daily':
      // Next weekday
      if (!isWeekday(nextDate)) {
        nextDate = nextDate.day(1); // Next Monday
      }
      break;
      
    case 'Weekly':
      // Next occurrence of preferred day
      const targetDay = moment().day(job.preferredDay).day();
      if (nextDate.day() <= targetDay) {
        nextDate.day(targetDay);
      } else {
        nextDate.add(1, 'week').day(targetDay);
      }
      break;
      
    case 'Bi-Weekly':
      // Next occurrence of preferred day on correct week type
      const targetBiWeeklyDay = moment().day(job.preferredDay).day();
      const targetWeekType = job.evenOrOddWeeks?.toLowerCase() === 'even';
      
      nextDate.day(targetBiWeeklyDay);
      while ((nextDate.week() % 2 === 0) !== targetWeekType || nextDate.isSameOrBefore(startDate)) {
        nextDate.add(1, 'week');
      }
      break;
      
    case 'Twice a Week':
      // Next occurrence of any preferred day
      if (job.preferredDays?.length) {
        const dayNumbers = job.preferredDays.map(day => moment().day(day).day());
        let found = false;
        
        for (let i = 0; i < 7; i++) {
          if (dayNumbers.includes(nextDate.day()) && nextDate.isAfter(startDate)) {
            found = true;
            break;
          }
          nextDate.add(1, 'day');
        }
        
        if (!found) {
          nextDate = startDate.clone().day(dayNumbers[0]);
        }
      }
      break;
      
    case 'Every Other Day (M-W-F)':
      const mwfDays = [1, 3, 5]; // Monday, Wednesday, Friday
      while (!mwfDays.includes(nextDate.day()) || nextDate.isSameOrBefore(startDate)) {
        nextDate.add(1, 'day');
      }
      break;
      
    case 'Monthly':
      nextDate = startDate.clone().add(1, 'month').startOf('month');
      if (job.preferredDay) {
        nextDate.day(job.preferredDay);
        if (nextDate.date() > 7) {
          nextDate.add(1, 'week');
        }
      }
      break;
      
    case 'Quarterly':
      const currentQuarter = startDate.quarter();
      nextDate = startDate.clone().quarter(currentQuarter + 1).startOf('quarter');
      if (job.preferredDay) {
        nextDate.day(job.preferredDay);
        if (nextDate.date() > 7) {
          nextDate.add(1, 'week');
        }
      }
      break;
      
    default:
      // For on-demand or unknown frequencies
      nextDate = startDate.clone().add(1, 'day');
      if (!isWeekday(nextDate)) {
        nextDate.day(1); // Next Monday
      }
  }
  
  return nextDate.toDate();
};

/**
 * Calculate days between two dates
 */
export const daysBetween = (date1, date2) => {
  return Math.abs(moment(date1).diff(moment(date2), 'days'));
};

/**
 * Check if job should be scheduled on given date
 */
export const shouldScheduleJob = (job, date, lastServiceDate) => {
  const dayOfWeek = getDayOfWeek(date);
  const weekType = getWeekType(date);
  
  switch (job.frequency) {
    case 'Daily':
      return isWeekday(date);
      
    case 'Weekly':
      return dayOfWeek === job.preferredDay;
      
    case 'Bi-Weekly':
      return dayOfWeek === job.preferredDay && 
             weekType.toLowerCase() === job.evenOrOddWeeks?.toLowerCase();
      
    case 'Twice a Week':
      return job.preferredDays?.includes(dayOfWeek);
      
    case 'Every Other Day (M-W-F)':
      return ['Monday', 'Wednesday', 'Friday'].includes(dayOfWeek);
      
    case 'Monthly':
      if (!lastServiceDate) return true;
      return daysBetween(date, lastServiceDate) >= ROUTE_CONFIG.FREQUENCIES.SPACING_DAYS.Monthly;
      
    case 'Quarterly':
      if (!lastServiceDate) return true;
      return daysBetween(date, lastServiceDate) >= ROUTE_CONFIG.FREQUENCIES.SPACING_DAYS.Quarterly;
      
    default:
      return false;
  }
};

/**
 * Format date for display
 */
export const formatDate = (date, format = 'MMM dd, yyyy') => {
  return moment(date).format(format);
};

/**
 * Get start and end of week
 */
export const getWeekBounds = (date) => {
  const start = moment(date).startOf('isoWeek');
  const end = moment(date).endOf('isoWeek');
  
  return {
    start: start.toDate(),
    end: end.toDate(),
    startFormatted: start.format('YYYY-MM-DD'),
    endFormatted: end.format('YYYY-MM-DD')
  };
};

/**
 * Check if date is in current week
 */
export const isInCurrentWeek = (date, weekStart, weekEnd) => {
  if (!weekStart || !weekEnd) {
    console.log('⚠️ isInCurrentWeek - weekStart or weekEnd is null');
    return false;
  }
  const dateMoment = moment(date);
  const isInRange = dateMoment.isSameOrAfter(weekStart) && dateMoment.isSameOrBefore(weekEnd);
  console.log('📅 isInCurrentWeek check:', {
    date: dateMoment.format('YYYY-MM-DD'),
    weekStart: moment(weekStart).format('YYYY-MM-DD'),
    weekEnd: moment(weekEnd).format('YYYY-MM-DD'),
    isInRange
  });
  return isInRange;
};

/**
 * Get dates for next N days (weekdays only)
 */
export const getNextWeekdays = (startDate, count) => {
  const dates = [];
  let current = moment(startDate);
  
  while (dates.length < count) {
    if (isWeekday(current)) {
      dates.push(current.toDate());
    }
    current.add(1, 'day');
  }
  
  return dates;
};

/**
 * Parse service period to date range
 */
export const parseServicePeriod = (servicePeriod, year) => {
  const periods = {
    'Q1': { start: `${year}-01-01`, end: `${year}-03-31` },
    'Q2': { start: `${year}-04-01`, end: `${year}-06-30` },
    'Q3': { start: `${year}-07-01`, end: `${year}-09-30` },
    'Q4': { start: `${year}-10-01`, end: `${year}-12-31` },
    'January': { start: `${year}-01-01`, end: `${year}-01-31` },
    'February': { start: `${year}-02-01`, end: `${year}-02-28` },
    'March': { start: `${year}-03-01`, end: `${year}-03-31` },
    'April': { start: `${year}-04-01`, end: `${year}-04-30` },
    'May': { start: `${year}-05-01`, end: `${year}-05-31` },
    'June': { start: `${year}-06-01`, end: `${year}-06-30` },
    'July': { start: `${year}-07-01`, end: `${year}-07-31` },
    'August': { start: `${year}-08-01`, end: `${year}-08-31` },
    'September': { start: `${year}-09-01`, end: `${year}-09-30` },
    'October': { start: `${year}-10-01`, end: `${year}-10-31` },
    'November': { start: `${year}-11-01`, end: `${year}-11-30` },
    'December': { start: `${year}-12-01`, end: `${year}-12-31` }
  };
  
  return periods[servicePeriod] || null;
};
